import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  // ChevronRight removed — breadcrumbs now use the shared <Breadcrumbs />
  // component (rendered via PageHero), which supplies its own separators.
  Sparkles,
  MessageSquare,
  TrendingUp,
  ShieldCheck,
  Building2,
  MapPin,
  MousePointerClick,
  Layers,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { LazyMount } from "@/components/LazyMount";
import { PageHero } from "@/components/PageHero";
import { MeetChrisVideoButton } from "@/components/MeetChrisVideoButton";
import { StickyCTABar } from "@/components/StickyCTABar";
import { FinalCTA } from "@/components/home/HomeSections";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  type IndustryServiceCombo,
  getSiblingServiceCombosForIndustry,
  getSiblingIndustryCombosForService,
} from "@/lib/industryServiceData";
import { FreeAuditCTABanner } from "@/components/FreeAuditCTABanner";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import { ogImageFor, ogMetaTags } from "@/lib/ogImages";
import { getPageLastUpdated } from "@/lib/lastUpdatedRegistry";
import { StatewideCoverageStrip } from "@/components/StatewideCoverageStrip";
import { GenericCaseStudyStrip } from "@/components/GenericCaseStudyStrip";
import { MeetChrisStrip } from "@/components/MeetChrisStrip";
import {
  SITE_URL,
  SCHEMA_ORG_ID,
  SCHEMA_LOCALBUSINESS_ID,
  LOCAL_BUSINESS_SCHEMA,
  buildSpeakableSchema,
} from "@/lib/entitySchema";

type Props = { combo: IndustryServiceCombo };

export function IndustryServicePage({ combo }: Props) {
  const { industry, service, modifier, path } = combo;
  const Icon = service.icon;

  // Merge industry FAQs (already industry-specific) with the service's
  // service-specific FAQs (with industry name templated in) for unique FAQ
  // content per page that's also FAQPage-schema worthy.
  const mergedFaqs = [
    ...service.serviceFaqs.map((f) => ({
      q: f.q(industry.name),
      a: f.a(industry.name, industry.shortName),
    })),
    ...industry.faqs.slice(0, 3),
  ];

  const siblingServices = getSiblingServiceCombosForIndustry(
    industry.slug,
    service.slug,
  );
  const siblingIndustries = getSiblingIndustryCombosForService(
    service.slug,
    industry.slug,
  );

  // ---- JSON-LD ----
  // BreadcrumbList mirrors the on-page <Breadcrumbs />:
  // Home → Industries → {Industry} → {Service}. Crawlers get the same
  // hierarchy users see and earn breadcrumb rich-results in SERPs.
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "Industries",
        item: `${SITE_URL}/industries`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: industry.name,
        item: `${SITE_URL}/industries/${industry.slug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: `${service.serviceName} for ${industry.name}`,
        item: `${SITE_URL}${path}`,
      },
    ],
  };

  const pageUrl = `${SITE_URL}${path}`;
  const pageHeadline = `${service.serviceName} for ${industry.name}`;
  const pageDescription = service.meta.description(
    industry.name,
    industry.shortName,
  );
  const lastUpdated = getPageLastUpdated(pageUrl);

  // Service node — references the canonical LocalBusiness via @id so Google
  // stitches this offering to the same entity as the rest of the site.
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${pageUrl}#service`,
    name: pageHeadline,
    serviceType: service.serviceName,
    description: pageDescription,
    url: pageUrl,
    areaServed: { "@type": "State", name: "Colorado" },
    provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
    audience: {
      "@type": "BusinessAudience",
      audienceType: industry.shortName,
      name: `${industry.name} businesses`,
    },
    category: industry.name,
    datePublished: "2025-01-01",
    dateModified: lastUpdated,
  };

  // Article node — gives AI crawlers (Perplexity, AI Overviews, ChatGPT) a
  // freshness signal and an authored entity to cite alongside Service.
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${pageUrl}#article`,
    headline: pageHeadline,
    description: pageDescription,
    mainEntityOfPage: pageUrl,
    author: { "@id": SCHEMA_ORG_ID },
    publisher: { "@id": SCHEMA_ORG_ID },
    datePublished: "2025-01-01",
    dateModified: lastUpdated,
  };

  const faqJsonLd = mergedFaqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: mergedFaqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  // WebPage node ties the page together: references the Service as
  // mainEntity, the BreadcrumbList, and adds speakable selectors for voice.
  const webPageJsonLd = {
    ...buildSpeakableSchema(pageUrl, pageHeadline),
    "@id": `${pageUrl}#webpage`,
    description: pageDescription,
    breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
    mainEntity: { "@id": `${pageUrl}#service` },
    about: { "@id": SCHEMA_ORG_ID },
    dateModified: lastUpdated,
  };

  const breadcrumbJsonLdWithId = { ...breadcrumbJsonLd, "@id": `${pageUrl}#breadcrumb` };

  const heroTitleParts = service.heroTitle(industry.name);

  return (
    <SiteLayout>
      {/* JSON-LD: BreadcrumbList + LocalBusiness (canonical entity) +
          Service (this offering) + FAQPage + WebPage with speakable. All
          nodes are linked via @id so Google can resolve the graph. */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLdWithId) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(LOCAL_BUSINESS_SCHEMA) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />



      <PageHero
        eyebrow={service.heroEyebrow(industry.name)}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Industries", to: "/industries" },
          { label: industry.name, to: `/industries/${industry.slug}` },
          { label: `${service.serviceName} for ${industry.shortName}` },
        ]}
        title={
          <>
            {heroTitleParts.lead}
            <span className="text-gradient-gold">{heroTitleParts.gold}</span>
            {heroTitleParts.trail}
          </>
        }
        description={service.heroDescription(industry.name, industry.shortName)}
        hideTrustSignals
      >

        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            {service.ctaLabel}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" /> (719) 419-3935
          </a>
        </div>

        <div className="mt-4 flex justify-center">
          <MeetChrisVideoButton variant="light" />
        </div>

        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-2 text-sm font-semibold text-brand-cream/85 sm:grid-cols-3">
          {[
            "Colorado-only agency",
            `${industry.name} specialists`,
            "Direct owner access",
          ].map((d) => (
            <div
              key={d}
              className="flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2"
            >
              <CheckCircle2 className="h-4 w-4 text-brand-gold" />
              {d}
            </div>
          ))}
        </div>
      </PageHero>

      <PricingTeaserStrip />


      {/* What it is + why it matters */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              <Icon className="h-4 w-4" /> {service.serviceName} for {industry.name}
            </span>
            <h2 className="mt-3 text-4xl font-extrabold leading-tight text-foreground">
              Why {industry.name.toLowerCase()} businesses need this
            </h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground">
              <p>{service.whyItMatters(industry.name, industry.shortName)}</p>
              <p>{modifier}</p>
            </div>
          </div>

          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              The {industry.name} Pain
            </span>
            <h3 className="mt-3 text-2xl font-extrabold text-foreground">
              What we hear from Colorado {industry.shortName}s
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              These come up on almost every call we have with a Colorado{" "}
              {industry.shortName}. For the full picture of how we help this
              industry, see our{" "}
              <Link
                to="/industries/$industrySlug"
                params={{ industrySlug: industry.slug }}
                className="font-semibold text-brand-gold-deep underline-offset-4 hover:underline"
              >
                {industry.name} marketing hub
              </Link>
              .
            </p>
            <ul className="mt-6 space-y-4">
              {industry.pains.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5 shadow-card"
                >
                  <MessageSquare className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-deep" />
                  <span className="text-base font-medium text-foreground">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What we do — service pillars (industry-tailored) */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              How We {service.verb}
            </span>
            <h2 className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
              {service.serviceName} built for{" "}
              <span className="text-gradient-gold">{industry.name.toLowerCase()}</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              {service.whatItIs}
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {service.pillars.map((pillar, i) => (
              <div
                key={pillar.title}
                className="rounded-2xl border border-border bg-card p-7 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
              >
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-gold text-brand-black shadow-gold">
                  <span className="text-lg font-black">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-5 text-xl font-bold text-foreground">{pillar.title}</h3>
                <p className="mt-2 leading-relaxed text-muted-foreground">
                  {pillar.body(industry.name, industry.shortName)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry-specific "what we do for {industry}" — pulled from
          industriesData.ts whatWeDo[]. This gives each industry-service
          page real industry-tailored content even before per-combo
          modifiers are written. */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              {industry.name} Playbook
            </span>
            <h2 className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
              The way we work with{" "}
              <span className="text-gradient-gold">Colorado {industry.shortName}s</span>
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {industry.whatWeDo.map((w) => (
              <div
                key={w.title}
                className="rounded-2xl border border-border bg-card p-7 shadow-card"
              >
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-secondary text-brand-gold">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-foreground">{w.title}</h3>
                <p className="mt-2 leading-relaxed text-muted-foreground">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust + pricing nudge */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="grid gap-10 rounded-2xl border border-brand-gold/30 bg-gradient-to-br from-brand-gold/10 via-brand-gold/5 to-transparent p-8 lg:grid-cols-[1.4fr_1fr] lg:items-center lg:p-12">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                <ShieldCheck className="h-4 w-4" /> Honest, public pricing
              </span>
              <h3 className="mt-3 text-3xl font-extrabold text-foreground sm:text-4xl">
                See exactly what {service.serviceName.toLowerCase()} costs for your{" "}
                {industry.shortName}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                No "we have to talk to you first" runaround. We publish every package and price
                publicly. Pick what fits — or build a custom plan in our calculator.
              </p>
              <ul className="mt-5 grid gap-2 text-sm font-medium text-foreground sm:grid-cols-2">
                {[
                  "Month-to-month, no contracts",
                  "Talk directly to the owner",
                  "Cancel anytime",
                  "Real Colorado team",
                ].map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-gold-deep" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <Link
                to="/pricing"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-6 py-4 text-base font-bold uppercase tracking-wider text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                See {service.serviceName} Pricing
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-foreground/20 bg-background px-6 py-4 text-base font-semibold text-foreground transition-colors hover:border-brand-gold hover:text-brand-gold-deep"
              >
                See All Packages
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 text-sm font-bold text-brand-gold-deep hover:underline"
              >
                Or talk to the owner first
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sibling services — internal linking silo within the same industry */}
      {siblingServices.length > 0 && (
        <LazyMount minHeight={400} rootMargin="600px 0px">
          <section className="bg-background py-20 lg:py-28">
            <div className="mx-auto max-w-6xl px-5 lg:px-8">
              <div className="mx-auto max-w-3xl text-center">
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                  <TrendingUp className="h-4 w-4" /> Full Marketing Stack
                </span>
                <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                  Other ways we help Colorado{" "}
                  <span className="text-gradient-gold">{industry.shortName}s</span>
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  {service.serviceName} is one piece. Most {industry.shortName}s combine 2–3
                  services for the fastest growth — every page below is built specifically for
                  your industry.
                </p>
              </div>
              <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {siblingServices.map((c) => {
                  const SiblingIcon = c.service.icon;
                  return (
                    <li key={c.path}>
                      <Link
                        to={c.path as "/"}
                        className="group flex h-full items-start gap-4 rounded-2xl border border-border bg-card p-5 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
                      >
                        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-secondary text-brand-gold">
                          <SiblingIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-base font-extrabold text-foreground">
                              {c.service.serviceName} for {c.industry.name}
                            </span>
                            <ArrowRight className="h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                          </div>
                          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                            {c.service.whatItIs.split(" — ")[1] ?? c.service.whatItIs}
                          </p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
        </LazyMount>
      )}

      {/* Sibling industries — internal linking silo within the same service */}
      {siblingIndustries.length > 0 && (
        <LazyMount minHeight={400} rootMargin="600px 0px">
          <section className="bg-section-alt py-20 lg:py-28">
            <div className="mx-auto max-w-6xl px-5 lg:px-8">
              <div className="mx-auto max-w-3xl text-center">
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                  <Building2 className="h-4 w-4" /> Other Industries
                </span>
                <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                  {service.serviceName} for other{" "}
                  <span className="text-gradient-gold">Colorado industries</span>
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  We build dedicated {service.serviceName.toLowerCase()} pages for every
                  priority industry we serve. Pick yours below.
                </p>
              </div>
              <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {siblingIndustries.map((c) => (
                  <li key={c.path}>
                    <Link
                      to={c.path as "/"}
                      className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card transition-spring hover:-translate-y-0.5 hover:border-brand-gold/50 hover:shadow-card-hover"
                    >
                      <div className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-secondary text-brand-gold">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-bold text-foreground">
                            {c.service.serviceName} for {c.industry.name}
                          </span>
                          <ArrowRight className="h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-8 text-center">
                <Link
                  to="/industries"
                  className="inline-flex items-center gap-2 text-sm font-bold text-brand-gold-deep hover:underline"
                >
                  See every Colorado industry we serve
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>
        </LazyMount>
      )}

      {/* Investment teaser — industry-specific starting range, links to /pricing */}

      <FreeAuditCTABanner
        headline={`Get your free ${industry.shortName} marketing audit`}
      />

      <GenericCaseStudyStrip industryHint={industry.shortName} />

      {/* FAQs */}
      {mergedFaqs.length > 0 && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                FAQs
              </span>
              <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                {service.serviceName} for {industry.name} —{" "}
                <span className="text-gradient-gold">questions</span>
              </h2>
            </div>
            <Accordion type="single" collapsible className="mt-10">
              {mergedFaqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-bold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      <StatewideCoverageStrip
        serviceSlug={service.slug}
        contextLabel={`${service.serviceName.toLowerCase()} for ${industry.shortName}s`}
      />



      {/* ============================================================
          RELATED RESOURCES — contextual internal-linking silo
          ------------------------------------------------------------
          Four cards, every link uses an industry-qualified anchor so
          the keyword appears in the link text (not just generic
          "learn more"). Order is intentional:
            1. Parent industry hub (vertical authority)
            2. Matching service hub (service authority)
            3. Google Maps / Local SEO (highest-converting service)
            4. PPC (paid alternative)
          When this page IS the Google Maps or PPC page, swap that
          card to the next-best companion service so we never link
          a page to itself.
      ============================================================ */}
      {(() => {
        const isLocalSeoPage = service.slug === "local-seo";
        const isPpcPage = service.slug === "ppc";

        type LinkCard = {
          to: string;
          params?: Record<string, string>;
          eyebrow: string;
          title: string;
          desc: string;
          Icon: typeof Layers;
        };

        const cards: LinkCard[] = [
          {
            to: "/industries/$industrySlug",
            params: { industrySlug: industry.slug },
            eyebrow: "Industry Hub",
            title: `Full ${industry.name} marketing playbook`,
            desc: `Everything we do for Colorado ${industry.shortName}s — services, results, FAQs, and city coverage in one place.`,
            Icon: Building2,
          },
          {
            to: service.hubPath,
            eyebrow: `${service.serviceName} Service`,
            title: `${service.serviceName} services in Colorado`,
            desc: `The full ${service.serviceName.toLowerCase()} service overview — how we work, what's included, and pricing.`,
            Icon: Layers,
          },
        ];

        if (!isLocalSeoPage) {
          cards.push({
            to: "/colorado-springs-google-maps",
            eyebrow: "Highest ROI",
            title: `Google Maps ranking for ${industry.shortName}s`,
            desc: `Most ${industry.shortName} leads come from the Map Pack. See exactly how we get Colorado ${industry.shortName}s into the top 3.`,
            Icon: MapPin,
          });
        } else {
          cards.push({
            to: "/colorado-springs-seo",
            eyebrow: "Pairs Well",
            title: `Organic SEO for ${industry.shortName}s`,
            desc: `Map Pack rankings compound when paired with organic search. See our full SEO service for Colorado ${industry.shortName}s.`,
            Icon: TrendingUp,
          });
        }

        if (!isPpcPage) {
          cards.push({
            to: "/colorado-springs-ppc",
            eyebrow: "Faster Results",
            title: `Google Ads for ${industry.shortName}s`,
            desc: `Need leads this week, not in 6 months? See how we run profitable Google Ads campaigns for Colorado ${industry.shortName}s.`,
            Icon: MousePointerClick,
          });
        } else {
          cards.push({
            to: "/colorado-springs-google-maps",
            eyebrow: "Pairs Well",
            title: `Google Maps for ${industry.shortName}s`,
            desc: `Pair PPC with Map Pack rankings to capture both paid and free clicks on every Colorado ${industry.shortName} search.`,
            Icon: MapPin,
          });
        }

        return (
          <section className="bg-background py-20 lg:py-28">
            <div className="mx-auto max-w-6xl px-5 lg:px-8">
              <div className="mx-auto max-w-3xl text-center">
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                  <Sparkles className="h-4 w-4" /> Keep Reading
                </span>
                <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                  Related resources for{" "}
                  <span className="text-gradient-gold">
                    Colorado {industry.shortName}s
                  </span>
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  The pages most {industry.shortName} owners read next — parent
                  industry hub, matching service, and the two highest-converting
                  channels.
                </p>
              </div>

              <ul className="mt-10 grid gap-4 sm:grid-cols-2">
                {cards.map((c) => (
                  <li key={`${c.to}-${c.params?.industrySlug ?? ""}`}>
                    <Link
                      to={c.to}
                      params={c.params}
                      className="group flex h-full items-start gap-4 rounded-2xl border border-border bg-card p-5 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
                    >
                      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-secondary text-brand-gold">
                        <c.Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
                          {c.eyebrow}
                        </span>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <span className="text-base font-extrabold text-foreground">
                            {c.title}
                          </span>
                          <ArrowRight className="h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                        </div>
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {c.desc}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        );
      })()}

      {/* Final CTA strip */}
      <section className="bg-gradient-dark py-16 text-brand-cream lg:py-20">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-5 text-center lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-brand-gold">
            <Sparkles className="h-3.5 w-3.5" /> Ready when you are
          </span>
          <h2 className="text-3xl font-extrabold leading-tight text-white sm:text-4xl lg:text-5xl">
            Let's grow your Colorado{" "}
            <span className="text-gradient-gold">{industry.shortName}</span>
          </h2>
          <p className="max-w-2xl text-base text-brand-cream/80 sm:text-lg">
            Free strategy session. Real recommendations. No pressure — and a clear "not a
            fit" if that's the truth.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold uppercase tracking-wider text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              {service.ctaLabel}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" /> (719) 419-3935
            </a>
          </div>
        </div>
      </section>

      <MeetChrisStrip />
      <FinalCTA />
      <StickyCTABar />
    </SiteLayout>
  );
}

/* ============================================================
   HEAD BUILDER — used by the dynamic route
============================================================ */
export function buildIndustryServiceHead(combo: IndustryServiceCombo) {
  const { industry, service, path } = combo;
  // Map our internal service slugs to the OG image bucket so the share image
  // matches the service shown on the page (SEO art for /seo-for-X, web-design
  // art for /web-design-for-X, etc.). `local-seo` shares the Google Maps OG.
  const ogServiceKey = (
    {
      seo: "seo",
      "web-design": "web-design",
      "local-seo": "google-maps",
      ppc: "ppc",
    } as const
  )[service.slug as "seo" | "web-design" | "local-seo" | "ppc"] ?? "seo";
  const ogImage = `${SITE_URL}${ogImageFor({ service: ogServiceKey })}`;
  const title = service.meta.title(industry.name);
  const description = service.meta.description(industry.name, industry.shortName);
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}${path}` },
      ...ogMetaTags(ogImage),
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${path}` }],
  };
}
